import { Subscription } from "~/server/models/account"

// Shared logic for deriving the date window a client is entitled to for a dataset,
// from their subscription filter policies. Used by both the "Your Access" card
// (DatasetInfoCards) and the date-range picker (FilterDateRangePicker) so the picker
// can default to and stay within the period the client can actually query.

export type Bounds = { lowers: Date[]; uppers: Date[]; equals: Date[] }

export const parseDate = (v?: string): Date | null => {
  if (!v) return null
  const parsed = new Date(v)
  return isNaN(parsed.getTime()) ? null : parsed
}

// Determine the base date field names for a dataset (datetime vs date variants).
export const getDateFieldNames = (
  dataset: any,
): { startDateField: string; endDateField: string } => {
  const hasDatetimeColumns = dataset?.dataListing?.deliveryColumns?.some(
    (column: any) =>
      column?.targetColumnName === "observation_start_datetime" ||
      column?.targetColumnName === "observation_end_datetime",
  )
  return {
    startDateField: hasDatetimeColumns
      ? "observation_start_datetime"
      : "observation_start_date",
    endDateField: hasDatetimeColumns
      ? "observation_end_datetime"
      : "observation_end_date",
  }
}

// Extract the subscription filter clauses (and combining logic) that apply to a
// given dataset for the provided subscriptions.
export const getSubscriptionFilterClauses = (
  subscriptions: Subscription[] | undefined,
  dataset: any,
): { filterClauses: any[]; filterLogic: string | undefined } => {
  const dataProductListingName = dataset?.dataListing?.dataProductListingName
  const dataListingName = dataset?.dataListing?.name
  const resourceName =
    dataProductListingName && dataListingName
      ? `data-products/${dataProductListingName}/data/${dataListingName}`
      : undefined
  if (!resourceName) return { filterClauses: [], filterLogic: undefined }

  const matchingPolicies = (subscriptions || [])
    .flatMap((s) => s.resources || [])
    .filter((r: any) => r?.resourceName === resourceName)
    .map((r: any) => r?.subscriptionFilterPolicy)
    .filter(Boolean)

  const filterClauses = matchingPolicies.flatMap(
    (policy: any) => policy?.filterClauses || [],
  )
  const filterLogic = matchingPolicies.find(
    (policy: any) => policy?.filterLogic,
  )?.filterLogic

  return { filterClauses, filterLogic }
}

const DAY_MS = 24 * 60 * 60 * 1000

export const computeBounds = (
  dateFilters: any[],
  startDateField: string,
  endDateField: string,
): Bounds => {
  const lowers: Date[] = []
  const uppers: Date[] = []
  const equals: Date[] = []
  for (const f of dateFilters) {
    const value = parseDate(f?.value)
    if (!value) continue
    const op = f?.operator
    if (!op) continue
    if (op === "==") equals.push(value)
    // Normalize exclusive operators to inclusive bounds so the displayed and
    // selectable range reflects the last/first day that actually has data:
    // `observation_end_date < Apr 1` means data through Mar 31, and
    // `observation_start_date > X` means data from the day after X.
    if (f?.fieldName === startDateField) {
      if (op === ">=") lowers.push(value)
      if (op === ">") lowers.push(new Date(value.getTime() + DAY_MS))
    }
    if (f?.fieldName === endDateField) {
      if (op === "<=") uppers.push(value)
      if (op === "<") uppers.push(new Date(value.getTime() - DAY_MS))
    }
  }
  return { lowers, uppers, equals }
}

export interface EntitledDateRange {
  // Lower/upper bound of the entitled window (null = open-ended on that side).
  start: Date | null
  end: Date | null
  // True when the subscription actually restricts the date range for this dataset.
  hasRestriction: boolean
  // True only for the "normal" cases the picker can safely constrain to:
  // a single contiguous interval, a single inequality, or a single equality.
  // Complex policies (OR logic, mixed equals + bounds, contradictory bounds)
  // return false so callers fall back to dataset-only behaviour.
  isSimpleInterval: boolean
}

// Derive the date window a client is entitled to for a dataset. Returns
// hasRestriction=false when there are no date filters (full access).
export const getEntitledDateRange = (
  subscriptions: Subscription[] | undefined,
  dataset: any,
): EntitledDateRange => {
  const { startDateField, endDateField } = getDateFieldNames(dataset)
  const { filterClauses, filterLogic } = getSubscriptionFilterClauses(
    subscriptions,
    dataset,
  )

  const dateFilters = filterClauses.filter(
    (f: any) =>
      f?.fieldName === startDateField || f?.fieldName === endDateField,
  )

  if (dateFilters.length === 0) {
    return { start: null, end: null, hasRestriction: false, isSimpleInterval: true }
  }

  const hasOr = !!filterLogic && /\bOR\b/i.test(filterLogic)
  const { lowers, uppers, equals } = computeBounds(
    dateFilters,
    startDateField,
    endDateField,
  )

  // Equality-only: a single same-day grant.
  if (equals.length > 0 && lowers.length === 0 && uppers.length === 0) {
    const first = equals[0]
    const allSame = equals.every((d) => d.getTime() === first.getTime())
    if (allSame && !hasOr) {
      return { start: first, end: first, hasRestriction: true, isSimpleInterval: true }
    }
    return { start: null, end: null, hasRestriction: true, isSimpleInterval: false }
  }

  const start =
    lowers.length > 0
      ? new Date(Math.max(...lowers.map((d) => d.getTime())))
      : null
  const end =
    uppers.length > 0
      ? new Date(Math.min(...uppers.map((d) => d.getTime())))
      : null

  const isSimpleInterval =
    !hasOr &&
    equals.length === 0 &&
    (start !== null || end !== null) &&
    (start === null || end === null || start.getTime() <= end.getTime())

  return { start, end, hasRestriction: true, isSimpleInterval }
}
